import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, NoData, RowsSkeletonLoader } from '@node-space/ui-components'
import { EarnCustomerRewardsProgram } from './components/EarnCustomerRewardsProgram'
import { EarnDistributionDialog } from './components/EarnDistributionDialog'
import { EarnMyAssets } from './components/EarnMyAssets'
import { EarnNoPortfolio } from './components/EarnNoPortfolio'
import { EarnNoWallets } from './components/EarnNoWallets'
import { EarnPageHeader } from './components/EarnPageHeader'
import { EarnSummary } from './components/EarnSummary'
import { EarnTotalEarnings } from './components/EarnTotalEarnings'
import { EarnSheetProvider, useEarnSheetContext } from './context/EarnSheetProvider'
import { useEarnPageData } from './hooks/useEarnPageData'
import { useEarnTracking } from './hooks/useEarnTracking'
import { EARN_DASHBOARD_STATE, EARN_SHEET_ACTION_TYPE, EarnAsset } from './types'

export const Earn = () => (
  <EarnSheetProvider>
    <EarnContent />
  </EarnSheetProvider>
)

const EarnContent = () => {
  const { t } = useTranslation()
  const { trackPageView } = useEarnTracking()
  const { handleSheetOpen } = useEarnSheetContext()
  const [isDistributionDialogOpen, setIsDistributionDialogOpen] = useState(false)

  const handleDeposit = useCallback(
    (asset: EarnAsset) => {
      handleSheetOpen({
        type: EARN_SHEET_ACTION_TYPE.DEPOSIT,
        header: t('earn.depositSheet.header'),
        selectedCurrency: asset.asset,
        portfolioId: asset.portfolioId ?? undefined,
        productName: asset.productName,
      })
    },
    [handleSheetOpen, t]
  )

  const handleWithdraw = useCallback(
    (asset: EarnAsset) => {
      handleSheetOpen({
        type: EARN_SHEET_ACTION_TYPE.WITHDRAW,
        header: t('earn.withdrawSheet.header'),
        selectedCurrency: asset.asset,
        portfolioId: asset.portfolioId ?? undefined,
        productName: asset.productName,
      })
    },
    [handleSheetOpen, t]
  )

  const {
    dashboardState,
    featuredProduct,
    isLoading,
    isStatisticsLoading,
    isDirectEarning,
    canManageDistribution,
    assets,
    epcTakesEarn,
    summary,
    totalEarnings,
    customerRewardsProgram,
  } = useEarnPageData({ onDeposit: handleDeposit, onWithdraw: handleWithdraw })

  useEffect(() => {
    trackPageView('home')
  }, [trackPageView])

  const myAssetsTable = <EarnMyAssets assets={assets} showCustomerEarnRate={!isDirectEarning} />

  if (isLoading) {
    return <RowsSkeletonLoader rows={10} />
  }

  if (dashboardState === EARN_DASHBOARD_STATE.NO_PRODUCTS) {
    return <NoData dataName="earning products" description={t('earn.noProductsDescription')} />
  }

  if (dashboardState === EARN_DASHBOARD_STATE.NO_WALLETS && featuredProduct) {
    return <EarnNoWallets featuredProduct={featuredProduct} />
  }

  if (dashboardState === EARN_DASHBOARD_STATE.NO_PORTFOLIOS) {
    return <EarnNoPortfolio assets={assets} showCustomerEarnRate={!isDirectEarning} />
  }

  if (isDirectEarning) {
    return (
      <>
        <EarnPageHeader />
        <Box flexCol gap={24}>
          <EarnSummary
            allocatedFunds={summary.allocatedFunds}
            availableFunds={summary.availableFunds}
            totalAllTimeEarnings={summary.totalAllTimeEarnings}
            isAvailableFundsLoading={isStatisticsLoading}
          />
          {myAssetsTable}
        </Box>
      </>
    )
  }

  return (
    <>
      <EarnPageHeader />
      <Box flexCol gap={24}>
        <Box flex gap={24} className="items-stretch max-lg:flex-col">
          <Box flexCol gap={24} className="flex-1 min-w-0">
            <EarnSummary
              allocatedFunds={summary.allocatedFunds}
              availableFunds={summary.availableFunds}
              isAvailableFundsLoading={isStatisticsLoading}
            />
            <EarnTotalEarnings
              allocationEarnings={totalEarnings.allocationEarnings}
              customerEarnings={totalEarnings.customerEarnings}
              totalEarnings={totalEarnings.totalEarnings}
            />
          </Box>
          <Box className="flex-1 min-w-0">
            <EarnCustomerRewardsProgram
              onDistributeEarningsClick={() => setIsDistributionDialogOpen(true)}
              totalBalance={customerRewardsProgram.totalBalance}
              epcTakesEarn={epcTakesEarn}
              canManageDistribution={canManageDistribution}
              retainedEarningsPercentage={customerRewardsProgram.retainedEarningsPercentage}
              optInRateLabel={customerRewardsProgram.optInRateLabel}
              distributedEarnings={customerRewardsProgram.distributedEarnings}
              optedInTotal={customerRewardsProgram.optedInTotal}
              remainingTotal={customerRewardsProgram.remainingTotal}
              isStatisticsLoading={isStatisticsLoading}
            />
          </Box>
        </Box>
        {myAssetsTable}
        <EarnDistributionDialog
          open={isDistributionDialogOpen}
          onClose={() => setIsDistributionDialogOpen(false)}
        />
      </Box>
    </>
  )
}
